/**
 * German is the source language; this file is the reference for every other
 * one. Where another file is missing a key, this text is shown instead.
 *
 * Key layout: <area>.<thing>. Placeholders in {curly braces} are filled in at
 * runtime.
 */
export const de = {
  // ------------------------------------------------------------- General
  'app.name': 'UFS Atlas',
  'app.tagline': 'Ultimate Fishing Simulator 1',
  'app.searchPlaceholder': 'Fisch, Köder, Spot oder Methode suchen …',
  'app.loading': 'Wird geladen …',
  'app.back': '← Zurück',
  'app.ok': 'Ok',
  'app.close': 'Schließen',
  'app.save': 'Speichern',
  'app.cancel': 'Abbrechen',
  'app.copy': 'Kopieren',
  'app.copied': '✓ Kopiert',
  'app.none': '–',
  'app.offline':
    'Konten und Gruppen brauchen den Server. Öffne den Guide dafür über {url} statt als lokale Datei.',

  // ---------------------------------------------------------- Navigation
  'nav.start': 'Start',
  'nav.fisheries': 'Reviere',
  'nav.species': 'Arten',
  'nav.baits': 'Köder',
  'nav.stats': 'Statistik',
  'nav.profile': 'Profil',
  'nav.login': 'Anmelden',
  'nav.sources': 'Quellen',
  'nav.caughtTotal': 'Gefangene Arten insgesamt',
  'nav.toStart': 'Zur Startseite',
  'nav.menu': 'Menü',
  'nav.openMenu': 'Menü öffnen',
  'nav.closeMenu': 'Menü schließen',

  // ----------------------------------------------------------- Fisheries
  'map.overview': 'Gesamtübersicht',
  'map.maps': 'Karten',
  'map.spotsFromFiles': 'Spots laut Spieldaten',
  'map.speciesHere': 'Arten in diesem Revier',
  'map.style': 'Angelstil',
  'map.caughtOf': '{done} von {total} gefangen',
  'map.travelPoints': '{n} Reisepunkte aus den Spieldateien',
  'map.swarms': 'Fischschwärme ({n})',
  'map.hoverHint': 'Punkt überfahren für Details, klicken zum Filtern',
  'map.guideOnly': 'nur Guide',
  'map.dlcSpecies': 'DLC-Art',
  'map.fishHere': '{n} Fische',
  'map.noProjection':
    'Bei diesem Revier lassen sich die Weltkoordinaten der Schwärme nicht verlässlich auf das Kartenbild projizieren. Spotnummern und die Artenzuordnung je Spot stimmen trotzdem – nur die zusätzlichen Schwarm-Punkte bleiben ausgeblendet.',
  'map.entries': '{n} Einträge',
  'map.syncNote': 'Stand aus deinem Konto übernommen ({when}).',
  'map.profileTitle': 'Dein Profil – die Adresse lässt sich weitergeben',
  'map.readNote':
    'Spots, Artenlisten, Gewicht, Länge, Beißzeiten und die Köderinteressen kommen direkt aus den Spieldateien – die Prozentwerte im Block „Aus den Spieldateien“ sind die Zahlen des Spiels. Hakengröße, Führung und Tiefenangabe darüber sind Community-Erfahrungswerte.',
  'map.emptySpotNote':
    'Fische bewegen sich. Bei leerem Spot: Hunter Vision bzw. Fischfinder nutzen, Hakengröße reduzieren oder die Karte neu laden.',
  'map.guideOnlyNote':
    '„nur Guide“ heißt: Die Szene des Spiels enthält für diese Art keine Spawnpunkte. Das betrifft vor allem die Arten aus dem New-Fish-Species-DLC, die das Spiel erst zur Laufzeit ergänzt. Spotangabe stammt dann aus der Community-Recherche.',
  'map.announcedTitle': 'Noch keine belastbaren Guide-Daten',
  'map.announcedText':
    'Italy DLC ist auf Steam angekündigt, aber ohne veröffentlichten Termin und ohne spielbare Spotdaten.',
  'map.noHits': 'Keine Treffer. Filter zurücksetzen oder einen kleineren Suchbegriff verwenden.',
  'map.hookAdviceTitle': 'Hakenwahl und leere Spots',
  'map.hookAdvice':
    'Die Hakenangaben sind Community-Bereiche und oft auf große Exemplare ausgelegt. Meldet das Spiel „Haken zu groß“, beginne zwei bis vier Stufen kleiner – die Längen- und Gewichtsspanne im Spieldaten-Block ist dafür der beste Anhaltspunkt. Vergrößere erst, nachdem du die Zielart am Spot sicher gefangen hast. Bei Naturködern erhöhen drei Köderstücke den Anziehungsradius; bei Kunstködern zählen Geschwindigkeit und Rollenübersetzung zusammen.',
  'map.filterCount': '{shown} von {total}',
  'map.onlySpecies': 'nur {name}',
  'map.onlySpot': 'nur Spot {n}',
  'map.spread': 'frei verteilt',
  'map.morePanel': '+ {n} weitere Arten',
  'map.dlcToggle': 'New-Species-DLC',
  'map.footer':
    'UFS Atlas · Guide-Stand {guide} · Spieldaten {game} · Fan-Projekt, nicht offiziell mit den Entwicklern verbunden.',
  'map.filter': 'Filter',
  'map.readHead': 'So liest du die Angaben',
  'map.filterAll': 'Alle',
  'map.filterConfHigh': 'hohes Vertrauen',
  'map.filterConfMedium': 'mittleres Vertrauen',
  'map.filterConfLow': 'abgeleitete Werte',
  'map.filterAllSpecies': 'alle Arten',
  'map.filterOpen': 'nur fehlende',
  'map.filterCaught': 'nur gefangene',
  'map.favorites': 'Favoriten',
  'map.compact': 'Kompakt',
  'map.spotList': 'Spot {list}',
  'map.groupBase': 'Basis',
  'map.groupVariant': 'Variante',
  'map.groupDlc': 'DLC',
  'map.groupAnnounced': 'Angekündigt',

  // Species that exist only in the game files: placeholders instead of guide text
  'gameOnly.spots': 'Spots {list}',
  'gameOnly.seeMap': 'siehe Karte',
  'gameOnly.hook': 'an die Fischgröße anpassen',
  'gameOnly.depth': 'Schwarmtiefe im Spiel prüfen',
  'gameOnly.method': 'nicht im Guide erfasst',
  'gameOnly.notes':
    'Diese Art steht in den Spieldateien dieses Reviers, aber noch nicht im recherchierten Guide.',
  'map.alt': 'Revierkarte aus den Spieldateien',
  'map.spotTitle': 'Spot {n}',
  'map.spotHead': 'SPOT {n}',
  'map.moreSpecies': '+ {n} weitere Arten',
  'map.noShoals': 'Keine Schwärme in Wurfweite',
  'map.shoals': 'Fischschwärme ({n})',
  'map.greenMarks': 'Grün markiert: Spots mit {name}',
  'map.clearPick': 'Auswahl aufheben',
  'map.spotPanelHead': 'Spot {n} · {count} Arten in Reichweite',
  'map.fishCount': '{n} Fische',
  'map.atSpot': 'am Spot',
  'map.metres': '~{n} m',
  'map.noShoalsHere':
    'Keine Schwarmpunkte in Wurfweite – hier wird geschleppt oder vom Boot gefischt.',
  'map.boatOnly':
    'Für dieses Revier enthalten die Spieldateien keine Kartenpunkte – hier wird ausschließlich vom Boot aus gefischt.',

  // ---------------------------------------------------------------- Fish
  'fish.spot': 'Spot',
  'fish.hook': 'Haken',
  'fish.bestTime': 'Beste Zeit',
  'fish.bestMethod': 'Beste Angelart',
  'fish.bestRetrieve': 'Beste Führung',
  'fish.baits': 'Köder',
  'fish.groundbait': 'Grundfutter / Anfütterung',
  'fish.fromGameFiles': 'Aus den Spieldateien',
  'fish.weight': 'Gewicht',
  'fish.length': 'Länge',
  'fish.sizes': 'Passende Größenstufen',
  'fish.weather': 'Wetter',
  'fish.yourRecord': 'Dein Rekord',
  'fish.caught': 'gefangen',
  'fish.open': 'offen',
  'fish.gameOnly': 'nur Spieldaten',
  'fish.markCaught': 'Als gefangen markieren',
  'fish.favorite': 'Favorit',
  'fish.spotN': 'Spot {n}',
  'fish.shoalPoints': 'Schwarmpunkte hier',
  'fish.fishHere': 'Fische hier',
  'fish.dlcSpecies': 'DLC-Art',
  'fish.dlcNote': '– ohne feste Spawnpunkte in der Szene',
  'fish.method': 'Angelart',
  'fish.baitInterest': 'Köder, Interesse laut Prefab',
  'fish.retrieve': 'Führung beim Spinnfischen',
  'fish.catches': 'Fänge',
  'fish.recordFishery': 'Rekordrevier',
  'fish.practiceNote': 'Praxisnotiz: ',
  'fish.sources': 'Quellen: ',
  'fish.gameFiles': 'Spieldaten',
  'fish.confidence': 'Vertrauen: {level}',
  'fish.confHigh': 'hoch',
  'fish.confMedium': 'mittel',
  'fish.confLow': 'abgeleitet',
  'fish.openSpecies': 'Alles zur Art',

  // ------------------------------------------------------------- Methods
  'method.fly': 'Fliege',
  'method.lure': 'Kunstköder',
  'method.natural': 'Naturköder',
  'method.boilie': 'Boilie',
  'method.flyRod': 'Fliegenrute',
  'method.spinRod': 'Spinnrute',
  'method.floatGround': 'Pose / Grund',
  'method.groundRig': 'Grundmontage',
  'method.threshold':
    'Das Spiel verlangt Wetter × Vorliebe × Schnur ≥ 0,4 für einen Biss (Casual 0,29).',
  'method.needsWeather': 'Mit {method} reicht ein Wetterwert ab {pct} %.',
  'method.hopeless': 'Keine Angelart kommt hier ohne perfekte Bedingungen über die Schwelle.',
  'method.threePieces':
    'Drei Köderstücke am Haken heben den Naturköder auf bis zu {pct} %.',

  // ---------------------------------------------------------- Statistics
  'stats.title': 'Statistik',
  'stats.level': 'Level',
  'stats.points': 'Punkte',
  'stats.species': 'Arten',
  'stats.fisheriesComplete': 'Reviere komplett',
  'stats.catches': 'Fänge',
  'stats.bites': 'Bisse',
  'stats.totalWeight': 'Masse gesamt',
  'stats.time': 'Angelzeit',
  'stats.heaviest': 'Schwerster',
  'stats.longest': 'Längster',
  'stats.loadSave': 'Spielstand laden',
  'stats.reset': 'Zurücksetzen',
  'stats.resetAsk': 'Lokalen Stand samt Haken zurücksetzen?',
  'stats.signedInAs': '◔ angemeldet als {name}',
  'stats.emptyTitle': 'Noch kein Spielstand geladen',
  'stats.emptyText':
    'Diese Seite wird vollständig aus deinem Spielstand gefüllt: Fänge je Revier, Bisse, Angelzeit, Punkte und dein größter Fisch je Art. Es wird nichts hochgeladen, die Datei wird nur im Browser gelesen.',
  'stats.emptyAccount':
    'Mit einem Konto liegt der Stand zusätzlich auf dem Server – dann kannst du dich in Gruppen vergleichen.',
  'stats.angler': 'Angler',
  'stats.xp': '{n} EP',
  'stats.money': 'Geld',
  'stats.luckStrength': 'Glück {luck} % · Kraft {strength} %',
  'stats.catchesTotal': 'Fänge gesamt',
  'stats.bitesN': '{n} Bisse',
  'stats.caughtWeight': 'Gefangenes Gewicht',
  'stats.hitRate': '{pct} % Trefferquote',
  'stats.speciesCaught': '{n} Arten gefangen',
  'stats.tabFisheries': 'Reviere',
  'stats.tabBiggest': 'Größte Fische',
  'stats.tabSets': 'Rutensets',
  'stats.noSets': 'Der Spielstand enthält keine gespeicherten Rutensets.',
  'stats.setsHint':
    'Die fünf Sets aus dem Spiel mit Rute, Rolle, Schnur, Pose, Haken, Ködern und Montage. Hakenstufe, Tiefe und Schrot sind die zuletzt eingestellten Werte des jeweiligen Sets.',
  'stats.ownedGear': 'Gekaufte Ausrüstung',
  'stats.colFishery': 'Revier',
  'stats.colFish': 'Fische',
  'stats.colRate': 'Quote',
  'stats.colTime': 'Zeit',
  'stats.colWeight': 'Gewicht',
  'stats.colBiggest': 'Größter Fang',
  'stats.sum': 'Summe',
  'stats.colSpecies': 'Art',
  'stats.colYourRecord': 'Dein Rekord',
  'stats.colPossible': 'Möglich',
  'stats.colShare': 'Ausschöpfung',
  'stats.colCatches': 'Fänge',
  'stats.colTotal': 'Gesamt',
  'stats.colRecordFishery': 'Rekordrevier',

  // --------------------------------------------- Spielstand und Konto
  'sync.aheadTitle': 'Hier liegt der neuere Stand.',
  'sync.aheadText':
    'In deinem Konto steht ein älterer Spielstand ({when}). Er wurde nicht über diesen hier geschrieben – du kannst den neueren hochladen.',
  'sync.aheadTextEmpty':
    'In deinem Konto steht noch kein Spielstand. Du kannst den hier geladenen hochladen.',
  'sync.upload': 'In mein Konto hochladen',
  'sync.uploading': 'Wird hochgeladen …',
  'sync.later': 'Später',
  'sync.uploadLead':
    'Die Datei direkt an den Server schicken – sie wird dort gelesen und ersetzt dein Profil. Für den regelmäßigen Upload ohne Klicken steht die Anleitung darunter.',
  'sync.pickFile': 'PROFILE-Datei hochladen',
  'sync.uploadedN': 'Übernommen: {n} Arten stehen jetzt in deinem Konto.',
  'sync.title': 'Spielstand hochladen',

  // Wo die Spielstände liegen, je System
  'paths.windows': 'Windows',
  'paths.mac': 'macOS',
  'paths.linux': 'Linux · Proton',
  'paths.copy': 'Ordnerpfad kopieren',

  // Changelog
  'changelog.title': 'Änderungen',
  'legal.version': 'Version {v}',
  'changelog.lead':
    'Was sich mit jeder Fassung geändert hat. „Allgemein“ gilt überall, „Offline-Version“ betrifft den Download, „Serverversion“ die Seite mit Konto.',
  'changelog.general': 'Allgemein',
  'changelog.offline': 'Offline-Version',
  'changelog.server': 'Serverversion',

  // Anleitung: Upload von allein
  'howto.title': 'Von allein hochladen',
  'howto.lead':
    'Ein Befehl, alle zwei Stunden ausgeführt – danach ist dein Profil immer auf dem Stand deiner letzten Angelsession. Der Token steckt im Befehl, halte ihn also für dich.',
  'howto.winNote':
    'Aufgabenplanung: den Befehl in einer Eingabeaufforderung als Administrator ausführen. Er legt die Aufgabe „UFS Atlas Upload“ an. Windows bringt curl seit Version 1803 selbst mit.',
  'howto.macNote':
    'cron: „crontab -e“ im Terminal öffnen und die Zeile eintragen. Beim ersten Lauf fragt macOS nach Zugriff auf den Ordner – einmal erlauben.',
  'howto.linuxNote':
    'cron: „crontab -e“ öffnen und die Zeile eintragen. Der Pfad zeigt in das Proton-Prefix von App 468920; bei einer Steam-Bibliothek auf einer anderen Platte liegt er unter deren steamapps/compatdata.',
  'howto.tail':
    'Jeder Upload ersetzt dein Profil vollständig. Läuft der Auftrag, während das Spiel gerade speichert, greift der nächste Lauf – es geht nichts verloren.',

  // ------------------------------------------------------- Save-file import
  'import.title': 'Spielstand einlesen',
  'import.lead':
    'Der Guide liest deinen Spielstand aus und hakt alle bereits gefangenen Arten ab. Die Datei bleibt lokal im Browser und wird nirgendwohin gesendet.',
  'import.whereLead': 'Zu finden unter:',
  'import.slots':
    'PROFILE_0, PROFILE_1 und PROFILE_2 sind die drei Profilslots des Spiels – nimm den, in dem du spielst. Die Dateien haben bewusst keine Endung.',
  'import.reading': 'Lese …',
  'import.pick': 'PROFILE-Datei wählen',
  'import.noCounters':
    'Keine Fangzähler gefunden. Bitte eine PROFILE-Datei wählen: PROFILE_0, PROFILE_1 oder PROFILE_2, jeweils ohne Endung.',
  'import.done': '{who}{n} gefangene Arten übernommen, inklusive persönlicher Rekorde.',
  'import.alsoServer': 'Auch im Konto gespeichert.',
  'import.serverFailed': '(Serverupload fehlgeschlagen: {error})',
  'import.unreadable': 'Datei konnte nicht gelesen werden: {error}',
  'import.unreadablePlain': 'Datei konnte nicht gelesen werden.',
  'import.byHand':
    'Alternativ hakst du Arten von Hand ab – der Stand wird im Browser gespeichert.',
  'import.resetAsk': 'Wirklich alle Haken und importierten Rekorde entfernen?',
  'import.reset': 'Fangliste zurücksetzen',

  // ---------------------------------------------------------------- Sources
  'sources.eyebrow': 'Recherchebasis',
  'sources.title': 'Quellen & Datenqualität',
  'source.official': 'Offiziell',
  'source.wiki': 'Community-Wiki',
  'source.test': 'Community-Test',
  'source.guide': 'Community-Guide',
  'source.secondary': 'Sekundärquelle',
  'source.observation': 'Community-Beobachtung',
  'sources.note':
    'Spots, Artenlisten, Gewichts- und Längenspannen, Beißzeitkurven und die Köderinteressen stammen direkt aus den installierten Spieldateien (Unity-Szenen, Fisch- und Köder-Prefabs, Lokalisierungstabelle). Haken- und Führungsempfehlungen bleiben Community-Erfahrungswerte; niedrig bewertete Angaben sind bewusst als Startpunkt markiert.',

  // ----------------------------------------------------------- All fisheries
  'overview.caught': 'Arten gefangen',
  'overview.caughtSub': '{pct} % der Artenliste',
  'overview.completeSub': 'alle Arten des Reviers gefangen',
  'overview.left': 'Noch offen',
  'overview.leftSub': 'Arten ohne Haken',
  'overview.perFishery': 'Fortschritt je Revier',
  'overview.colCaught': 'Gefangen',
  'overview.colProgress': 'Fortschritt',
  'overview.colOpen': 'Offen',

  // -------------------------------------------------------------- Account
  'auth.title': 'Anmelden',
  'auth.intro':
    'Kein Passwort: Du bekommst einen sechsstelligen Code per E-Mail. Mit dem Konto liegt dein Profil auf dem Server, du kannst Gruppen beitreten und dich vergleichen.',
  'auth.email': 'deine@mail.de',
  'auth.requestCode': 'Code anfordern',
  'auth.sending': 'Sende …',
  'auth.checking': 'Prüfung läuft …',
  'auth.codeSent': 'Code ist unterwegs. Er gilt 15 Minuten.',
  'auth.verify': 'Anmelden',
  'auth.verifying': 'Prüfe …',
  'auth.stayLoggedIn': 'Angemeldet bleiben',
  'auth.stayHint': '(90 Tage, nur auf diesem Gerät)',
  'auth.logout': 'Abmelden',
  'auth.codePlaceholder': '123456',
  'auth.altchaOk': 'Kein Bot – geprüft',
  'auth.altchaWork': 'Prüfung läuft … {pct} %',
  'auth.altchaPrepare': 'Prüfung wird vorbereitet …',
  'auth.altchaBy': 'ALTCHA · eigener Server',
  'auth.altchaNoCrypto': 'Dieser Browser stellt keine Kryptofunktionen bereit.',
  'auth.altchaFailed': 'Die Bot-Prüfung ging nicht auf. Bitte die Seite neu laden.',

  // --------------------------------------------------------------- Profile
  'profile.yours': 'Dein Profil',
  'profile.other': 'Profil',
  'profile.overview': 'Übersicht',
  'profile.records': 'Arten',
  'profile.missing': 'Was noch fehlt',
  'profile.duel': 'Vergleich mit dir',
  'profile.groups': 'Gruppen',
  'profile.followers': 'Follower',
  'profile.follows': 'Folgt',
  'profile.settings': 'Einstellungen',
  'profile.copyLink': 'Link kopieren',
  'profile.follow': 'Folgen',
  'profile.following': '✓ Du folgst',
  'profile.saveState': 'Stand des Spielstands',

  'profile.copyPrompt': 'Adresse zum Kopieren:',
  'profile.noSaveYet': 'ohne Spielstand',
  'profile.nComplete': '{n} komplett',
  'profile.nRecords': '{n} Rekorde',
  'profile.nSpecies': '{n} Arten',
  'profile.nPieces': '{n} Stück',
  'profile.settingsSub': 'Name, Token, Abmelden',
  'profile.anglerLine': 'Angler {name}',
  'profile.noPublicGroup': 'Dieser Angler ist in keiner öffentlichen Gruppe.',
  'profile.yourGroups': 'Deine Gruppen',
  'profile.duelSignIn': 'Melde dich an, dann wird dein Profil hier Wert für Wert gegen {name} gestellt.',
  'profile.duelNoSave':
    'Für den Vergleich fehlt dein eigener Spielstand. Lade ihn unter „Einstellungen“ hoch.',
  'profile.needsServer': 'Profile brauchen den Server. Öffne den Guide über {url}.',

  // Profile, overview and tables
  'profile.sortSum': 'Masse gesamt',
  'profile.sortBest': 'Bestmasse',
  'profile.sortLength': 'Länge',
  'profile.sortCount': 'Stück',
  'profile.sortName': 'Name',
  'profile.experience': '{n} Erfahrung',
  'profile.skills': 'Fähigkeiten',
  // The skill tree from the save file
  'skills.title': 'Fähigkeiten',
  'skills.unlocked': '{done} von {total} freigeschaltet',
  'skills.points': '{n} Punkte offen',
  'skills.step': 'Stufe {level} von {steps}',
  'skills.have': 'freigeschaltet',
  'skills.open': 'offen',

  // Rechtliches
  'legal.privacy': 'Datenschutz',
  'legal.imprint': 'Impressum',
  'legal.github': 'Quelltext auf GitHub',
  'cookie.text':
    'Diese Seite setzt zwei Cookies, und zwar erst wenn du dich anmeldest: eines für die Sitzung, eines für „angemeldet bleiben“. Keine Statistik, keine Werbung, keine fremden Dienste.',
  'cookie.ok': 'Verstanden',

  'privacy.title': 'Datenschutzerklärung',
  'privacy.lead':
    'Der Guide ist ein Fan-Projekt. Er kommt mit so wenig Daten aus wie möglich, und was er speichert, steht hier vollständig.',

  'privacy.responsibleTitle': 'Verantwortlich',
  'privacy.responsibleText':
    'Verantwortlich für diese Seite ist der Betreiber, dessen Anschrift im Impressum steht.',

  'privacy.shortTitle': 'Kurz gesagt',
  'privacy.shortText':
    'Ohne Konto verlässt kein einziger deiner Werte den Browser. Mit Konto liegen deine E-Mail-Adresse, dein Anzeigename und die Zahlen aus deinem hochgeladenen Spielstand auf dem Server. Es gibt keine Statistik-Werkzeuge, keine Werbung und keine eingebundenen Dienste Dritter.',

  'privacy.localTitle': 'Was im Browser bleibt',
  'privacy.localText':
    'Abgehakte Arten, Favoriten, die gewählte Sprache und die Werte eines geladenen Spielstands liegen im lokalen Speicher deines Browsers. Sie werden nicht übertragen. Löschst du die Websitedaten, sind sie weg.',
  'privacy.localSave':
    'Die PROFILE-Datei des Spiels wird im Browser gelesen. Hochgeladen wird sie nur, wenn du angemeldet bist und den Upload selbst auslöst.',

  'privacy.accountTitle': 'Konto',
  'privacy.accountWhat':
    'Zum Konto gehören: deine E-Mail-Adresse, ein Anzeigename, ein Zugriffstoken für den Upload ohne Anmeldung, der Zeitpunkt der letzten Anmeldung sowie – sobald du einen Spielstand überträgst – die daraus gelesenen Zahlen: Fänge, Rekorde, Angelzeit, Fortschritt je Revier, Ausrüstung, Rutensets und Fähigkeiten.',
  'privacy.accountWhy':
    'Grundlage ist die Erfüllung des von dir gewünschten Dienstes (Art. 6 Abs. 1 lit. b DSGVO). Ohne E-Mail-Adresse lässt sich kein Anmeldecode zustellen.',
  'privacy.accountKeep':
    'Die Daten bleiben, bis du das Konto löschst. Schreib dafür an die unten genannte Adresse; das Konto samt Profil wird dann entfernt.',

  'privacy.publicTitle': 'Was andere sehen',
  'privacy.publicText':
    'Dein Profil ist über seine Adresse erreichbar, sobald du sie weitergibst: Anzeigename, die Zahlen aus deinem Spielstand, deine gekaufte Ausrüstung, deine Rutensets, deine freigeschalteten Fähigkeiten und deine öffentlichen Gruppen. Deine E-Mail-Adresse ist nie sichtbar. In einer Gruppe sehen die Mitglieder deinen Namen und deine Kennzahlen.',

  'privacy.cookiesTitle': 'Cookies',
  'privacy.cookiesText':
    'Zwei Stück, beide erst nach dem Anmelden: das Sitzungs-Cookie (PHPSESSID, 30 Tage) und ufs_remember für „angemeldet bleiben“ (90 Tage). Beide sind httpOnly und SameSite=Lax. Sie sind für die Anmeldung technisch erforderlich; eine Einwilligung braucht es dafür nicht. Ohne Konto setzt die Seite kein Cookie.',

  'privacy.logsTitle': 'Server-Protokolle',
  'privacy.logsText':
    'Der Webserver protokolliert wie üblich Zugriffe mit IP-Adresse, Zeitpunkt, aufgerufener Adresse und Browserkennung. Das dient dem Betrieb und der Abwehr von Missbrauch (Art. 6 Abs. 1 lit. f DSGVO).',

  'privacy.mailTitle': 'E-Mail-Versand',
  'privacy.mailText':
    'Anmeldecodes verschickt der Server über einen E-Mail-Anbieter. Dabei werden deine E-Mail-Adresse und der Code übertragen. Welcher Anbieter das ist, steht im Impressum.',

  'privacy.thirdTitle': 'Keine Drittdienste',
  'privacy.thirdText':
    'Es werden keine Schriften, Skripte oder Bilder von fremden Servern geladen. Die Bot-Prüfung beim Anmelden (ALTCHA) rechnet im Browser und wird vom eigenen Server geprüft – ohne Captcha-Anbieter.',

  'privacy.rightsTitle': 'Deine Rechte',
  'privacy.rightsText':
    'Du kannst Auskunft, Berichtigung, Löschung, Einschränkung und Datenübertragbarkeit verlangen und der Verarbeitung widersprechen. Außerdem steht dir eine Beschwerde bei einer Datenschutz-Aufsichtsbehörde offen.',

  'privacy.contactTitle': 'Kontakt',
  'privacy.contactMissing': 'Die Kontaktadresse steht im Impressum.',
  'profile.luck': 'Glück {n}',
  'profile.strength': 'Stärke {n}',
  'profile.topSpecies': 'Stärkste Art',
  'profile.avgPerCatch': 'Ø {n} kg je Fang',
  'profile.catchesPerHourSub': '{n} Fänge je Stunde',
  'profile.colWeight': 'Masse',
  'profile.colBestLength': 'Beste Länge',
  'profile.colAvgPerPiece': 'Ø je Stück',
  'profile.colRecordFrom': 'Rekord aus',
  'profile.colDifference': 'Differenz',
  'profile.colCommonSpecies': 'Gemeinsame Arten',
  'profile.colDraw': 'unentschieden',
  'profile.colLead': 'Vorsprung',
  'profile.fisheryNote':
    'Masse in kg. „Arten“ zählt die Artenliste des Reviers, die Zahlen daneben stammen aus der Revierstatistik des Spielstands.',
  'profile.speciesNote': 'Bestmasse und Gesamtmasse in kg, Länge in cm.',
  'profile.onlyThem': 'Die hat nur {name}',
  'profile.onlyYou': 'Die hast nur du',
  'profile.speciesBalance': 'Artenbilanz',
  'profile.bothHave': 'Beide: {n}',
  'profile.onlyName': 'Nur {name}: {n}',
  'profile.onlyMe': 'Nur du: {n}',
  'profile.noneYet': 'Noch keiner: {n}',
  'profile.tie': 'Keine – ihr seid gleichauf.',
  'profile.loading': 'Wird geladen …',
  'profile.followerCount': 'Follower',
  'profile.followsCount': 'Folgt',
  'profile.saveVersion': ' · Spielstand {version}',
  'profile.menuFollow': '{followers} folgen · folgt {follows}',
  'profile.noSaveOther': 'Dieser Angler hat noch keinen Spielstand hochgeladen.',
  'profile.membersOf': '{n} Mitglieder',
  'profile.youAreAdmin': ' · du bist Admin',
  'profile.byOwner': ' · von {name}',
  'profile.shareOfList': '{pct} % der Artenliste',
  'profile.visitedFisheries': '{n} Reviere bereist',
  'profile.progressPerFishery': 'Fortschritt je Revier',
  'profile.recordsPerSpecies': 'Rekorde je Art',
  'profile.noSpeciesYet': 'Noch keine Art gefangen.',
  'profile.nOpen': '({n} offen)',
  'duel.figures': 'Kennzahlen',
  'duel.anglerLine': 'Angler {name} · Level {level}',
  'duel.stateLine': 'Stand: {when} ({ago})',
  'duel.heavierFish': 'Schwererer Fisch',
  'duel.onlyMe': 'Nur du: {n}',
  'duel.neitherYet': 'Noch keiner: {n}',
  'duel.theyMissIt': 'ihm fehlt sie',
  'duel.youMissIt': 'dir fehlt sie',
  'duel.noSpeciesInPick': 'Keine Art in dieser Auswahl.',
  'duel.speciesCount': '{shown} von {total} Arten, die mindestens einer von euch gefangen hat. ',

  // Account settings
  'account.nameSaved': 'Name gespeichert.',
  'account.nameHint':
    'Unter diesem Namen finden dich andere in der Suche und in Gruppen. Er ist einmalig – wenn ihn schon jemand hat, musst du dir einen anderen aussuchen.',
  'account.userName': 'Benutzername',
  'account.title': 'Konto',
  'account.signedInLabel': 'Angemeldet als',
  'account.namePlaceholder': 'dein Name',
  'account.shareTitle': 'Dein Profil teilen',
  'account.autoUpload': 'Spielstand automatisch hochladen',
  'account.localWithSave': '– dazu die Werte des zuletzt geladenen Spielstands.',
  'account.saveName': 'Namen speichern',
  'account.linkHint':
    'Diese Adresse zeigt dein Profil. Wer angemeldet ist und sie öffnet, sieht seinen eigenen Stand daneben – Kennzahl für Kennzahl, Revier für Revier und Art für Art.',
  'account.addressCopied': 'Adresse kopiert.',
  'account.viewProfile': 'Profil ansehen',
  'account.pushAsk': 'Der lokale Stand ersetzt dein Profil im Konto vollständig. Fortfahren?',
  'account.pushed': 'Übernommen: {n} Arten im Konto.',
  'account.push': 'Lokalen Stand ins Konto übernehmen',
  'account.localHas':
    'In diesem Browser sind {n} Arten abgehakt. Damit lässt sich das Profil im Konto füllen, ohne die PROFILE-Datei erneut zu laden.',
  'account.localEmpty':
    'In diesem Browser ist nichts gespeichert. Hake unter „Reviere“ Arten ab oder lade unter „Statistik“ einen Spielstand.',
  'account.tokenHint':
    'Mit diesem Befehl lädst du deinen Spielstand ohne Anmeldung hoch – etwa aus der Windows-Aufgabenplanung nach jeder Angelsession. Jeder Upload ersetzt dein Profil vollständig.',
  'account.slotLabel': 'Profilslot:',
  'account.uploadDone': 'Spielstand hochgeladen und in dein Profil übernommen.',
  'account.copyCommand': 'Befehl kopieren',
  'account.newToken': 'Token erneuern',
  'account.newTokenAsk': 'Neuen Token erzeugen? Der alte funktioniert danach nicht mehr.',
  'account.signedInAs': 'Angemeldet als {name}',
  'account.followersOf': 'Angler, die diesem Profil folgen',
  'account.followsOf': 'Profile, denen dieses Konto folgt',
  'account.manageGroups': 'Gruppen verwalten',
  'account.notInGroup': 'Du bist noch in keiner Gruppe.',

  // Comparing two profiles
  'duel.progress': 'Fortschritt',
  'duel.yield': 'Ausbeute',
  'duel.records': 'Rekorde',
  'duel.efficiency': 'Effizienz',
  'duel.speciesCaught': 'Arten gefangen',
  'duel.fisheriesVisited': 'Reviere bereist',
  'duel.atLeastOneBite': 'mindestens ein Biss',
  'duel.heaviestFish': 'Schwerster Fisch',
  'duel.longestFish': 'Längster Fisch',
  'duel.weightOneSpecies': 'Masse einer Art',
  'duel.sumStrongest': 'Summe der schwersten Art',
  'duel.bitesUsed': 'Verwertete Bisse',
  'duel.catchesPerBite': 'Fänge je Biss',
  'duel.catchesPerHour': 'Fänge je Stunde',
  'duel.weightPerCatch': 'Masse je Fang',
  'duel.pointsPerHour': 'Punkte je Stunde',
  'duel.level': 'gleichauf',
  'duel.youLead': '▲ du',
  'duel.theyLead': '▼ er',
  'duel.longerFish': 'Längerer Fisch',
  'duel.morePieces': 'Mehr Stück',
  'duel.fisheryByFishery': 'Revier für Revier',
  'duel.speciesBySpecies': 'Art für Art',
  'duel.filterAll': 'Alle',
  'duel.filterDiff': 'Unterschiede',
  'duel.filterBoth': 'Beide gefangen',
  'duel.filterHis': 'Nur er',
  'duel.filterMine': 'Nur ich',
  'duel.filterLead': 'Ich führe',
  'duel.filterBehind': 'Er führt',
  'profile.noSave': 'Noch kein Spielstand hochgeladen.',

  // ------------------------------------------------------------ Start page
  'start.eyebrow': 'Fan-Atlas · Ultimate Fishing Simulator 1',
  'start.headline': 'Die Zahlen kommen aus den Spieldateien.',
  'start.lead':
    'Spots, Artenlisten, Beißzeiten, Ködervorlieben und Größenstufen sind ausgelesen, nicht geschätzt. Was aus der Community-Recherche stammt, steht als solches dabei.',
  'start.ctaMaps': 'Reviere öffnen',
  'start.ctaSpecies': 'Arten ansehen',
  'start.ctaSave': 'Spielstand laden',
  'start.wallTitle': 'Beißzeit über 24 Stunden, je Art aus dem Spiel',
  'start.wallHint': 'dunkel = Nacht · zum Öffnen anklicken',

  'start.factFisheries': 'Reviere',
  'start.factSpecies': 'Arten',
  'start.factSpots': 'Spots',
  'start.factShoals': 'Schwarmpunkte',
  'start.factBaits': 'Köder',
  'start.factCurves': 'Beißzeitkurven',

  'start.featuresTitle': 'Was der Atlas kann',
  'start.mapsTitle': 'Reviere und Spots',
  'start.mapsText':
    '{maps} Karten mit den Spotnummern aus dem Spiel, den Schwarmpunkten auf dem Kartenbild und der Artenliste je Spot – mitsamt der Zahl der Fische, die dort stehen.',
  'start.mapsCta': 'Karten öffnen',
  'start.speciesTitle': 'Arten',
  'start.speciesText':
    '{species} Arten mit Gewichts- und Längenspanne, Beißzeitkurve, Wetterkurven und der Beschreibung aus der Enzyklopädie des Spiels.',
  'start.speciesCta': 'Artenliste',
  'start.baitsTitle': 'Köder und Angelart',
  'start.baitsText':
    'Für jede Art die Ködervorliebe in Prozent, dazu die beste Angelart – Fliege, Kunstköder, Pose oder Grund – und der Faktor jeder Führung beim Spinnfischen.',
  'start.baitsCta': 'Köderseite',
  'start.sizesTitle': 'Größenstufen',
  'start.sizesText':
    'Haken von #12 bis #12/0 mit der Gewichtsspanne, die sie fangen, dazu die Stufen für Kunstköder und Fliegen. {steps} Stufen, direkt aus dem FishManager.',
  'start.sizesCta': 'Zu den Arten',
  'start.saveTitle': 'Dein Spielstand',
  'start.saveText':
    'Die PROFILE-Datei laden und sehen, was fehlt: je Revier, je Art, mit deinem Rekord neben dem möglichen Maximum. Die Datei bleibt im Browser.',
  'start.saveCta': 'Spielstand laden',
  'start.profileTitle': 'Profil und Vergleich',
  'start.profileText':
    'Ein Konto gibt dir eine teilbare Profilseite. Wer sie öffnet und selbst angemeldet ist, sieht seinen Stand daneben – Kennzahl für Kennzahl, Revier für Revier, Art für Art.',
  'start.profileCta': 'Konto anlegen',
  'start.groupsTitle': 'Gruppen',
  'start.groupsText':
    'Öffentlich, nicht gelistet oder privat. Acht Ranglisten: schwerster und längster Fisch, Gesamtmasse, stärkste Art, Arten, komplette Reviere, Fänge, Angelzeit.',
  'start.groupsCta': 'Gruppen ansehen',
  'start.offlineTitle': 'Läuft auch ohne Server',
  'start.offlineText':
    'Der Guide ist fertiges HTML und JavaScript. Ohne Netz bleibt alles außer Konto und Gruppen benutzbar; abgehakte Arten liegen im Browser.',
  'start.offlineCta': 'Ausprobieren',

  'start.sourcesTitle': 'Woher die Zahlen kommen',
  'start.fromGame': 'Aus den Spieldateien',
  'start.fromGame1': 'Spotnummern und ihre Lage auf dem Kartenbild',
  'start.fromGame2': 'Artenliste je Revier und je Spot, mit der Zahl der Fische',
  'start.fromGame3': 'Gewichts- und Längenspanne, Beißzeitkurve, Wetterkurven',
  'start.fromGame4': 'Ködervorliebe je Art, Ködertyp, Führungsfaktoren',
  'start.fromGame5': 'Größenstufen für Haken, Kunstköder, Fliegen und Köder',
  'start.fromGame6': 'Namen und Beschreibungen aus der Lokalisierung',
  'start.fromCommunity': 'Aus der Community-Recherche',
  'start.fromCommunity1': 'Hakengrößen als Erfahrungswert je Art und Revier',
  'start.fromCommunity2': 'Tiefenangaben und Montagehinweise',
  'start.fromCommunity3': 'Strategien je Spot und Vertrauensstufe je Eintrag',
  'start.communityNote':
    'Diese Angaben sind im Guide als Community-Werte gekennzeichnet. Wo die Spieldateien etwas Genaueres hergeben, ersetzt es sie.',

  'start.openTitle': 'Offen und nachprüfbar',
  'start.openText':
    'Der Quelltext und die Werkzeuge, die die Spieldateien auslesen, liegen offen. Wer die Zahlen anzweifelt, kann sie mit derselben Spielinstallation nachziehen.',
  'start.openCta': 'Auf GitHub ansehen',
  'start.footer': 'Fan-Projekt, nicht mit den Entwicklern verbunden. Guide-Stand {guide}.',

  // ---------------------------------------------------------------- Groups
  'group.public': 'Öffentlich',
  'group.publicHint': 'steht im Verzeichnis, jeder darf beitreten',
  'group.unlisted': 'Nicht gelistet',
  'group.unlistedHint': 'nur über Link oder Code zu finden, Beitritt frei',
  'group.private': 'Privat',
  'group.privateHint': 'nur Mitglieder sehen sie, Beitritt nur mit Code',
  'group.create': 'Neue Gruppe',
  'group.join': 'Beitreten',
  'group.joinWithCode': 'Mit Code beitreten',
  'group.leave': 'Verlassen',
  'group.edit': 'Bearbeiten',
  'group.delete': 'Gruppe löschen',
  'group.members': 'Mitglieder',
  'group.admin': 'Gruppenadmin',
  'group.newCode': 'Neuer Code',
  'group.directory': 'Öffentliche Gruppen',
  'group.title': 'Gruppen',
  'group.yours': 'Deine Gruppen',
  'group.count': '{n} Stück',
  'group.memberCount': '{n} Mitglieder',
  'group.youAreAdmin': 'du bist Admin',
  'group.byOwner': 'von {name}',
  'group.needsAccount': 'Gruppen gibt es mit einem Konto.',
  'group.needsServer': 'Gruppen brauchen den Server. Öffne den Guide über {url}.',
  'group.noneYet': 'Du bist noch in keiner Gruppe. Leg eine an oder tritt einer öffentlichen bei.',
  'group.noneOther': 'Dieser Angler ist in keiner öffentlichen Gruppe.',
  'group.open': 'Öffnen',
  'group.allGroups': '← Alle Gruppen',
  'group.namePlaceholder': 'Name der Gruppe',
  'group.createHint':
    'Wer eine Gruppe anlegt, ist ihr Admin – verlässt er sie, wird sie aufgelöst.',
  'group.codePlaceholder': 'ABC123',
  'group.search': 'suchen …',
  'group.searchGo': 'Suchen',
  'group.noneFound': 'Keine öffentliche Gruppe gefunden.',
  'group.alreadyIn': '✓ dabei',
  'group.manage': 'Gruppen verwalten',
  'group.editTitle': 'Gruppe bearbeiten',
  'group.newCodeHint': 'Der alte Code gilt danach nicht mehr',
  'group.confirmLeave': 'Die Gruppe „{name}“ wirklich verlassen?',
  'group.confirmLeaveAdmin':
    'Du bist Admin von „{name}“. Verlässt du die Gruppe, wird sie aufgelöst. Fortfahren?',
  'group.confirmDelete': 'Die Gruppe „{name}“ endgültig auflösen?',
  'group.confirmKick': '{name} aus der Gruppe entfernen?',
  'group.kick': 'Entfernen',
  'group.code': 'Code {code}',
  'group.noData': 'Noch keine Daten',
  'group.done': 'Fertig',

  // Group boards
  'board.biggestFish': 'Schwerster Fisch',
  'board.longestFish': 'Längster Fisch',
  'board.totalWeight': 'Meiste Masse gesamt',
  'board.topSpeciesWeight': 'Meiste Masse einer Art',
  'board.species': 'Meiste Arten',
  'board.fisheriesComplete': 'Komplette Reviere',
  'board.fish': 'Meiste Fänge',
  'board.time': 'Meiste Angelzeit',

  // Member table columns
  'col.angler': 'Angler',
  'col.species': 'Arten',
  'col.fisheriesComplete': 'Reviere komplett',
  'col.catches': 'Fänge',
  'col.weight': 'Masse',
  'col.heaviest': 'Schwerster',
  'col.state': 'Stand',
  'col.noProfile': 'kein Profil',

  // Retrieve when spin fishing, in the order of the SpinningMethod enum
  'spin.straightSlow': 'Straight langsam',
  'spin.straight': 'Straight',
  'spin.straightFast': 'Straight schnell',
  'spin.liftDrop': 'Lift & Drop',
  'spin.stopGo': 'Stop & Go',
  'spin.twitching': 'Twitching',

  // Weather dials
  'weather.wind': 'Wind',
  'weather.windHint': 'windstill → stürmisch',
  'weather.clouds': 'Bewölkung',
  'weather.cloudsHint': 'klar → bedeckt',
  'weather.rain': 'Regen',
  'weather.rainHint': 'trocken → Dauerregen',

  'method.noChance': 'Ohne Aussicht: {list}.',

  // Species page
  'species.searchPlaceholder': 'Art suchen …',
  'species.onlyMissing': 'nur fehlende',
  'species.count': '{n} Arten',
  'species.noSize': 'ohne Größenangabe',
  'species.fisheries': 'Reviere',
  'species.noSpawns': 'keine Spawnpunkte',
  'species.backToList': 'Zurück zur Artenliste',
  'species.fromGuide': 'Aus der Community-Recherche',
  'species.unknown': 'Diese Art gibt es in den Spieldateien nicht.',
  'bait.backToList': 'Zurück zur Köderliste',
  'bait.interestTitle': 'Interesse je Art',
  'bait.openSpecies': 'Art öffnen',
  'bait.noSpecies': 'Für diesen Köder ist keine Art hinterlegt.',
  'bait.unknown': 'Diesen Köder gibt es in den Spieldateien nicht.',
  'species.method': 'Methode',
  'species.retrieve': 'Führung',

  'fish.allDay': 'rund um die Uhr gleich',
  'fish.hours24': '24 Uhr',
  'fish.showLess': 'weniger',
  'fish.showAllBaits': 'alle {n} Köder',
  'bait.baitSize': 'Ködergröße',

  // -------------------------------------------------------------- Bait page
  'bait.intro':
    'Jedes Köder-Prefab im Spiel führt eine Liste, wie stark sich welche Art für ihn interessiert – ein Wert zwischen 0 und 100 %. Diese Tabellen stehen hier unverändert. Sie sagen nichts über Hakengröße und Führung; das bleiben Erfahrungswerte.',
  'bait.searchPlaceholder': 'Köder oder Fisch suchen …',
  'bait.onlyFor': 'nur für {name}',
  'bait.countOf': '{shown} von {total} Ködern',
  'bait.speciesCount': '{n} Arten',
  'bait.strongest': 'am stärksten: {list}',
  // Art des Kunstköders, mit den Wörtern des Spiels
  'baitType.spinner': 'Spinner',
  'baitType.spoon': 'Blinker',
  'baitType.wobbler': 'Wobbler',
  'baitType.softBait': 'Gummi',
  'baitType.fly': 'Fliege',
  'baitType.flyDry': 'Trockenfliege',
  'baitType.flyWet': 'Nassfliege',
  'baitType.flyNymph': 'Nymphe',
  'baitType.flyStreamer': 'Streamer',
  'bait.noTableShort': 'keine Werte in den Spieldateien',
  'bait.noTableNote':
    'Die Spieldateien enthalten für diesen Köder keine Interessenwerte – alle 154 Einträge stehen auf 0. Das Spiel leitet das Interesse offenbar aus dem Fisch ab, aus dem der Schnitt stammt.',
  'bait.naturalNote':
    'Werden am Haken angeboten, mehrere Stücke vergrößern den Anziehungsradius.',
  'bait.boilieNote': 'Für Karpfen und Großfisch, an der Haarmontage.',
  'bait.flyNote': 'Vier Typen, unterschiedlich tief geführt.',
  'bait.lureNote':
    'Werden aktiv geführt; die Führungsart entscheidet mit über den Biss.',
  'bait.hookSizes': 'Hakengrößen',
  'bait.hookText':
    'Zu jeder Größe steht, welches Fischgewicht sie fängt; die Spanne wandert mit der Größe nach oben. Ein zu großer Haken lässt kleine Fische aus – daher die Meldung im Spiel, es mit einem kleineren zu versuchen. Kunstköder greifen erst ab #8, Fliegen ab #4.',
  'bait.colSize': 'Größe',
  'bait.colGap': 'Spalt',
  'bait.colHook': 'Haken · Fischgewicht',
  'bait.colBaitSize': 'Ködergröße · Fischlänge',
  'bait.biteTitle': 'Was der Biss sonst noch braucht',
  'bait.biteText':
    'Neben dem Köder gewichtet das Spiel neun Regler, jeder mit einer eigenen Kurve je Art: Uhrzeit, Hunger, Ködertiefe, Temperatur, Wind, Luftdruck, Bewölkung, Regen und Ködergeschwindigkeit. Gefüllt sind davon nur vier – Uhrzeit, Wind, Bewölkung und Regen. Die übrigen fünf stehen bei allen Arten auf der Konstanten 1 und wirken damit nicht.',
  'bait.depthLead': 'Insbesondere die Ködertiefe:',
  'bait.depthText':
    'sie entscheidet nicht darüber, ob ein Fisch beißt, sondern nur darüber, ob der Köder dort ankommt, wo der Schwarm steht. Tiefenangaben in Guides sind also Beobachtungen über den Standort der Fische, keine Vorliebe.',

  // Parts of a rod set, as the save file lists them
  'slot.rod': 'Rute',
  'slot.iceRod': 'Eisrute',
  'slot.reel': 'Rolle',
  'slot.line': 'Schnur',
  'slot.float': 'Pose',
  'slot.hook': 'Haken',
  'slot.boilie': 'Boilie',
  'slot.feeder': 'Feeder',
  'slot.feederBait': 'Feederköder',
  'slot.rodStand': 'Ständer',
  'slot.biteIndicator': 'Bissanzeiger',

  // Follows
  'follows.followYou': 'Folgen dir',
  'follows.followThem': 'Folgen {name}',
  'follows.youFollow': 'Du folgst',
  'follows.theyFollow': '{name} folgt',
  'follows.noneYou': 'Dir folgt noch niemand.',
  'follows.noneOther': 'Diesem Profil folgt noch niemand.',
  'follows.youNone': 'Du folgst noch niemandem.',
  'follows.otherNone': 'Dieses Profil folgt noch niemandem.',
  'follows.species': '{n} Arten',
  'follows.catches': '{n} Fänge',

  // Glossary: headings for the bait groups. The entries themselves come from the
  // game files; the guide names the groups.
  'gloss.natural.title': 'Naturköder',
  'gloss.natural.note': 'Werden am Haken angeboten, mehrere Stücke vergrößern den Anziehungsradius.',
  'gloss.boilie.title': 'Boilies',
  'gloss.boilie.note': 'Für Karpfen und Großfisch, an der Haarmontage.',
  'gloss.lure.title': 'Kunstköder-Arten',
  'gloss.lure.note': 'Werden aktiv geführt; die Führungsart entscheidet mit über den Biss.',
  'gloss.method.title': 'Angelmethoden',
  'gloss.method.note': 'Die fünf Spinnfisch-Führungen haben je drei Geschwindigkeitsstufen.',
  'gloss.gear.title': 'Montage & Ausrüstung',
  'gloss.gear.note': '',

  'gear.set': 'Set {n}',
  'gear.hookStep': 'Hakenstufe',
  'gear.depth': 'Tiefe',
  'gear.shot': 'Schrot',

  // Equipment classes from the save file
  'cat.ROD': 'Ruten',
  'cat.ICE_ROD': 'Eisruten',
  'cat.ROD_STAND': 'Ständer',
  'cat.REEL': 'Rollen',
  'cat.LINE': 'Schnüre',
  'cat.FLOAT': 'Posen',
  'cat.HOOK': 'Haken',
  'cat.BAIT': 'Naturköder',
  'cat.BOILIE': 'Boilies',
  'cat.FEEDER': 'Feeder',
  'cat.FEEDER_BAIT': 'Feederköder',
  'cat.BITE_INDICATOR': 'Bissanzeiger',
  'cat.LURE': 'Kunstköder',
  'cat.SPOON': 'Blinker',
  'cat.SPINNER': 'Spinner',
  'cat.WOBBLER': 'Wobbler',
  'cat.SOFT': 'Gummiköder',
  'cat.FLY': 'Fliegen',
  'cat.BOAT': 'Boote',
  'cat.DRILLER': 'Bohrer',
  'cat.FISHING': 'Kescher',
  'cat.GAS': 'Gaskocher',
  'cat.BEER': 'Getränke',
  'cat.FILLET': 'Filets',
  'cat.ICE': 'Eis-Ausrüstung',
} as const
